import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { sendPasswordResetEmail } from '@firebase/auth';
import { yupResolver } from '@hookform/resolvers/yup';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { navigateMypage } from '../../api/firebase/auth';
import { loginSchema } from '../../api/firebase/auth/validation';
import { auth, passwordSendSettings } from '../../api/firebase/config';
import {
  black,
  blackText,
  primaryButtonBackground,
  primarycolor,
  secondaryButtonnBackground
} from '../../constants/Colors';
import {
  buttonFont,
  cardContentPadding,
  cardSize,
  textFontSize,
  textMb,
  titleMb
} from '../../constants/Location';
import { AuthContext } from '../../contexts/auth';

import CustomLoading from './CustomLoading';
import CustomSnackbar from './CustomSnackbar';
import CustomTextfield from './CustomTextfield';

interface FormInputs {
  email: string;
}

const styles = {
  button: {
    '@media screen and (max-width:480px)': {
      width: 148
    },
    height: 42,
    width: 192
  },
  buttonFont,
  buttonWrapper: {
    display: 'flexbox',
    justifyContent: 'space-between'
  },
  cardContentPadding,
  cardSize,
  required: {
    color: primarycolor,
    marginLeft: '10px'
  },
  textFontSize,
  textMb,
  titleMb
};

const PasswordResetCard: React.FC = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const { currentUser } = useContext(AuthContext);

  const {
    control,
    watch,
    formState: { errors }
  } = useForm<FormInputs>({
    mode: 'onChange',
    resolver: yupResolver(loginSchema)
  });

  const HandleSubmit = async (): Promise<void> => {
    try {
      setLoading(true);
      await sendPasswordResetEmail(
        auth,
        watch('email'),
        passwordSendSettings()
      );
      navigate(`/auth/password_reset_complete?email=${watch('email')}`);
    } catch (e) {
      setOpen(true);
      setErrorText(t('register.error.common'));
    } finally {
      setLoading(false);
    }
  };

  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorText, setErrorText] = useState('');

  useEffect(() => {
    currentUser && navigateMypage(currentUser, navigate);
  }, []);

  const backPage = (): void => {
    navigate('/auth/login');
  };

  return (
    <>
      <CustomLoading loading={loading} />
      <Grid
        container
        direction="column"
        alignItems="center"
        justifyContent="center"
      >
        <Grid item xs={12}>
          <Card sx={styles.cardSize}>
            <CardContent sx={styles.cardContentPadding}>
              <Typography
                sx={[styles.titleMb, { color: blackText }]}
                component="div"
              >
                {t('passwordReset.title')}
              </Typography>
              <Typography
                sx={[styles.textFontSize, styles.textMb, { color: blackText }]}
              >
                {t('passwordReset.description')}
              </Typography>
              <Controller
                render={({ field }) => (
                  <CustomTextfield
                    {...field}
                    error={Boolean(errors.email)}
                    helperText={errors.email?.message}
                    fullWidth
                    labelText={t('form.email')}
                    requiredText={t('form.required')}
                    isRequired
                    placeholder="xxx@en-broker.co.jp"
                    sx={{
                      '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                        {
                          borderColor: black
                        }
                    }}
                  />
                )}
                control={control}
                name="email"
              />
              <CardActions
                sx={[styles.textMb, { marginTop: '32px', padding: '0px' }]}
              >
                <Grid
                  container
                  direction="row"
                  alignItems="center"
                  sx={styles.buttonWrapper}
                >
                  <Button
                    variant="contained"
                    sx={[secondaryButtonnBackground, styles.button]}
                    onClick={() => {
                      backPage();
                    }}
                  >
                    <Typography
                      component="div"
                      color={primarycolor}
                      sx={styles.buttonFont}
                    >
                      {t('passwordReset.back')}
                    </Typography>
                  </Button>
                  <Button
                    variant="contained"
                    sx={[primaryButtonBackground, styles.button]}
                    onClick={() => {
                      void (async () => {
                        await HandleSubmit();
                      })();
                    }}
                  >
                    <Typography
                      component="div"
                      color="white"
                      sx={styles.buttonFont}
                    >
                      {t('passwordReset.sendMail')}
                    </Typography>
                  </Button>
                </Grid>
              </CardActions>
            </CardContent>
          </Card>
          <CustomSnackbar
            open={open}
            autoHideDuration={6000}
            onClose={() => setOpen(false)}
            text={errorText}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default PasswordResetCard;
